/**
 * Order-related constants and pure utility functions.
 * Extracted from Orders.jsx to keep that file focused on UI logic.
 */

export const STATUSES = ['pending','confirmed','processing','shipped','delivered','cancelled','on_hold','revised'];

export const statusColors = {
  pending:'warning', confirmed:'info', processing:'info', shipped:'primary',
  delivered:'success', cancelled:'error', on_hold:'neutral', revised:'neutral',
};

export const ORDER_TYPES = [
  { value: 'invoice',          label: 'Tax Invoice' },
  { value: 'proforma_invoice', label: 'Proforma Invoice' },
  { value: 'purchase_order',   label: 'Purchase Order' },
];

export const CURRENCIES = ['USD','EUR','GBP','INR','JPY','AED','SGD'];

// Indian GST rates (standard slabs)
export const GST_RATES = [0, 5, 12, 18, 28];

// HSN prefix → GST rate mapping for pharma/chemical products
export const HSN_GST_MAP = {
  // Chapter 29 — Organic chemicals
  '2935': 12, '2936': 12, '2937': 12, '2938': 12, '2939': 12, '2941': 5,
  // Chapter 30 — Pharmaceutical products
  '3001': 12, '3002': 12, '3003': 12, '3004': 12, '3005': 12, '3006': 12,
  // Chapter 38
  '3822': 12,
  // 2-digit chapter fallbacks
  '29': 18, '30': 12, '38': 18,
};

/**
 * Suggest a GST rate from the HSN codes of the selected catalog items.
 * Returns null when no suggestion can be made.
 */
export function suggestGstRateFromHsn(items, catalogItems) {
  const rates = [];
  for (const it of items) {
    if (!it.catalog_item_id) continue;
    const cat = catalogItems.find(c => c.id === Number(it.catalog_item_id));
    const hsn = (cat?.hsn_code || '').trim().replace(/\s/g, '');
    if (!hsn) continue;
    const r4 = HSN_GST_MAP[hsn.slice(0, 4)];
    const r2 = HSN_GST_MAP[hsn.slice(0, 2)];
    const rate = r4 !== undefined ? r4 : r2;
    if (rate !== undefined) rates.push({ rate, hsn: hsn.slice(0, 4) || hsn.slice(0, 2) });
  }
  if (!rates.length) return null;
  const counts = rates.reduce((m, { rate }) => { m[rate] = (m[rate] || 0) + 1; return m; }, {});
  const best = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];
  const allSame = Object.keys(counts).length === 1;
  return { rate: Number(best[0]), allSame, hsns: [...new Set(rates.map(r => r.hsn))] };
}

export const GST_TYPES = [
  { value: 'igst',      label: 'IGST (Inter-state)' },
  { value: 'cgst_sgst', label: 'CGST + SGST (Intra-state)' },
  { value: 'export',    label: 'Export / LUT (0%)' },
  { value: 'none',      label: 'No Tax' },
];

export const CATALOG_CATEGORIES = [
  'impurity_standard','reference_standard','metabolite','degradation_product',
  'synthesis_intermediate','custom',
];
export const PHARMACOPEIAS = ['USP','EP','BP','JP','ICH','In-house','N/A'];

export const catLbl = c =>
  c.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

// PRASI LABS state: Telangana, GST state code 36
export const PRASI_STATE_CODE = '36';
export const PRASI_STATE_NAME = 'Telangana (TS-36)';

export function buildAddr(parts) {
  return parts.filter(p => p && String(p).trim()).map(p => String(p).trim()).join(', ');
}

export function genQuoteNumber() {
  const now = new Date();
  const yr  = now.getFullYear();
  const mo  = String(now.getMonth() + 1).padStart(2, '0');
  const rand = Math.floor(1000 + Math.random() * 9000);
  return `QUO-${yr}${mo}-${rand}`;
}

/**
 * Auto-fill party fields (GSTIN, address, phone) from a client/customer record.
 */
export function autoFillParty(party) {
  const updates = {};
  if (!party) return updates;

  const idType  = (party.company_id_type  || '').toUpperCase();
  const idValue = (party.company_id_value || '').trim();
  const isIndian = !party.country || party.country.trim().toLowerCase() === 'india';
  const isGSTIN = idType.includes('GST') || /^\d{2}[A-Z]{5}\d{4}[A-Z][A-Z\d]Z[A-Z\d]$/i.test(idValue);

  if (isGSTIN && idValue) {
    updates.client_gstin    = idValue;
    const stateCode = idValue.substring(0, 2);
    updates.client_state_code = /^\d{2}$/.test(stateCode) ? stateCode : (isIndian ? '00' : '');
  } else {
    updates.client_gstin      = '';
    updates.client_state_code = isIndian ? '00' : '';
  }

  updates.client_phone = party.phone || '';

  updates.billing_address = buildAddr([
    party.billing_address || party.address,
    party.billing_city    || party.city,
    party.billing_state   || party.state,
    party.billing_zip_code || party.zip_code,
    party.billing_country  || party.country,
  ]);

  updates.shipping_address = buildAddr([
    party.shipping_address || party.billing_address || party.address,
    party.shipping_city    || party.billing_city    || party.city,
    party.shipping_state   || party.billing_state   || party.state,
    party.shipping_zip_code || party.billing_zip_code || party.zip_code,
    party.shipping_country  || party.billing_country  || party.country,
  ]);

  return updates;
}

export function getTaxDefaults(currency, clientStateCode, currentRate) {
  const rate = Number(currentRate) > 0 ? Number(currentRate) : 18;
  if (currency !== 'INR') return { tax_type: 'none', tax_rate: 0, tax_label: '' };
  if (clientStateCode === PRASI_STATE_CODE) {
    return { tax_type: 'cgst_sgst', tax_rate: rate, tax_label: 'CGST+SGST' };
  }
  return { tax_type: 'igst', tax_rate: rate, tax_label: 'IGST' };
}

/** Derive the auto-determined GST type label + reason from form state */
export function gstAutoInfo(currency, clientStateCode, clientGstin) {
  if (currency !== 'INR') return null;
  const hasGstin  = !!clientGstin;
  const stateCode = (clientStateCode || '').trim();
  if (!hasGstin || !stateCode || stateCode === '00') {
    return { type: 'igst', label: 'IGST', reason: 'No client GSTIN — defaulting to inter-state', warn: true };
  }
  if (stateCode === PRASI_STATE_CODE) {
    return { type: 'cgst_sgst', label: 'CGST + SGST', reason: `Intra-state — client GST state ${stateCode} matches our state (${PRASI_STATE_NAME})`, warn: false };
  }
  return { type: 'igst', label: 'IGST', reason: `Inter-state — client state ${stateCode} ≠ our state ${PRASI_STATE_CODE} (${PRASI_STATE_NAME})`, warn: false };
}
